import React, { useMemo } from 'react';
import { Check, Calendar, HardDrive } from 'lucide-react';
import type { ApiPlan } from '../../api';
import {
  type BillingTerm,
  defaultBillingTerm,
  formatPlanPrice,
  getPricingOptions,
  perMonthEquivalent,
  termSavingsPercent,
  TERM_LABELS,
  type PlanPricingOption,
} from '../../lib/planPricing';

type PickerProps = {
  plan: ApiPlan | null | undefined;
  value: BillingTerm;
  onChange: (term: BillingTerm) => void;
  /** toggle = segmented pill; cards = full grid; list = stacked rows; compact = alias for list */
  variant?: 'toggle' | 'cards' | 'list' | 'compact';
  showSavings?: boolean;
};

function bestValueTerm(options: PlanPricingOption[]): BillingTerm | null {
  if (options.length < 2) return null;
  let best: BillingTerm | null = null;
  let bestPpm = Infinity;
  options.forEach((o) => {
    const ppm = perMonthEquivalent(o.priceInr, o.months);
    if (ppm < bestPpm) {
      bestPpm = ppm;
      best = o.term;
    }
  });
  return best;
}

export function PlanDurationPicker({
  plan,
  value,
  onChange,
  variant = 'cards',
  showSavings = true,
}: PickerProps) {
  const options = getPricingOptions(plan);
  const active = options.some((o) => o.term === value) ? value : defaultBillingTerm(plan);
  const bestTerm = useMemo(() => bestValueTerm(options), [options]);
  const monthlyRef = options.find((o) => o.term === 'monthly');
  const isList = variant === 'list' || variant === 'compact';

  if (options.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 px-3 py-4 text-center text-sm text-zinc-500">
        No pricing configured for this plan.
      </p>
    );
  }

  if (variant === 'toggle') {
    return (
      <div
        className="flex w-full flex-row gap-1 rounded-xl border border-zinc-200 bg-zinc-100/80 p-1 sm:gap-1.5 sm:rounded-2xl sm:p-1.5"
        role="radiogroup"
        aria-label="Billing duration"
      >
        {options.map((opt) => {
          const on = active === opt.term;
          const ppm = perMonthEquivalent(opt.priceInr, opt.months);
          const savings =
            showSavings && monthlyRef && opt.term !== 'monthly'
              ? termSavingsPercent(monthlyRef.priceInr, opt.priceInr, opt.months)
              : null;
          const isBest = bestTerm === opt.term && options.length > 1;

          return (
            <button
              key={opt.term}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(opt.term)}
              className={`relative flex min-w-0 flex-1 flex-col items-center justify-center rounded-lg px-1.5 py-2 text-center transition-all sm:rounded-xl sm:px-3 sm:py-2.5 ${
                on
                  ? 'bg-white shadow-sm ring-1 ring-emerald-500/40'
                  : 'text-zinc-500 hover:bg-white/60'
              }`}
            >
              {(isBest || (savings != null && savings > 0)) ? (
                <span
                  className={`absolute -top-1.5 right-1 rounded-full px-1 py-px text-[8px] font-bold uppercase leading-none text-white sm:-top-2 sm:right-2 sm:px-1.5 sm:py-0.5 sm:text-[9px] ${
                    isBest ? 'bg-amber-500' : 'bg-emerald-600'
                  }`}
                >
                  {isBest ? 'Best' : `−${savings}%`}
                </span>
              ) : null}
              <span className={`text-[11px] font-semibold leading-tight sm:text-sm ${on ? 'text-zinc-900' : 'text-zinc-600'}`}>
                {TERM_LABELS[opt.term]}
              </span>
              <span className={`mt-0.5 text-[10px] font-medium tabular-nums leading-tight sm:text-xs ${on ? 'text-emerald-700' : 'text-zinc-400'}`}>
                {formatPlanPrice(opt.priceInr)}
                {opt.months > 1 ? (
                  <span className="hidden font-normal sm:inline"> · {formatPlanPrice(ppm)}/mo</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  if (isList) {
    return (
      <div className="space-y-2" role="radiogroup" aria-label="Billing duration">
        {options.map((opt) => {
          const on = active === opt.term;
          const ppm = perMonthEquivalent(opt.priceInr, opt.months);
          const savings =
            showSavings && monthlyRef && opt.term !== 'monthly'
              ? termSavingsPercent(monthlyRef.priceInr, opt.priceInr, opt.months)
              : null;
          const isBest = bestTerm === opt.term && options.length > 1;

          return (
            <button
              key={opt.term}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(opt.term)}
              className={`flex w-full items-center gap-3 rounded-xl border-2 px-3 py-3 text-left transition sm:px-4 sm:py-3.5 ${
                on
                  ? 'border-emerald-500 bg-emerald-50/80 shadow-sm'
                  : 'border-zinc-200 bg-white hover:border-emerald-200 active:bg-zinc-50'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  on ? 'border-emerald-600 bg-emerald-600' : 'border-zinc-300 bg-white'
                }`}
              >
                {on ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : null}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-semibold text-zinc-900">{TERM_LABELS[opt.term]}</span>
                  {isBest ? (
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                      Best
                    </span>
                  ) : null}
                  {savings != null && savings > 0 && !isBest ? (
                    <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                      −{savings}%
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs text-zinc-500">
                  {opt.months > 1 ? `${formatPlanPrice(ppm)} per month` : 'Single payment'}
                </p>
              </div>

              <div className="shrink-0 text-right">
                <p className="text-base font-bold tabular-nums text-zinc-900 sm:text-lg">{formatPlanPrice(opt.priceInr)}</p>
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  const gridClass =
    options.length === 1
      ? 'grid grid-cols-1 gap-3'
      : options.length === 2
        ? 'grid grid-cols-2 gap-2.5 sm:gap-3'
        : 'grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3';

  return (
    <div className={gridClass} role="radiogroup" aria-label="Billing duration">
      {options.map((opt) => {
        const on = active === opt.term;
        const ppm = perMonthEquivalent(opt.priceInr, opt.months);
        const savings =
          showSavings && monthlyRef && opt.term !== 'monthly'
            ? termSavingsPercent(monthlyRef.priceInr, opt.priceInr, opt.months)
            : null;
        const isBest = bestTerm === opt.term && options.length > 1;
        const ribbon = isBest ? 'Best value' : savings != null && savings > 0 ? `Save ${savings}%` : null;

        return (
          <button
            key={opt.term}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(opt.term)}
            className={`group relative flex flex-col overflow-hidden rounded-2xl border p-4 pt-5 text-left transition-all duration-150 sm:p-5 sm:pt-6 ${
              on
                ? 'border-emerald-500 bg-gradient-to-b from-emerald-50 to-white shadow-lg shadow-emerald-600/10 ring-2 ring-emerald-500/25'
                : 'border-zinc-200 bg-white hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md'
            }`}
          >
            {/* Corner ribbon for best value / savings */}
            {ribbon ? (
              <span
                className={`absolute right-0 top-0 rounded-bl-xl px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white ${
                  isBest ? 'bg-gradient-to-r from-amber-500 to-amber-600' : 'bg-gradient-to-r from-emerald-500 to-teal-600'
                }`}
              >
                {ribbon}
              </span>
            ) : null}

            <div className="flex items-center gap-2">
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  on ? 'border-emerald-600 bg-emerald-600' : 'border-zinc-300 bg-white group-hover:border-emerald-300'
                }`}
              >
                {on ? <Check className="h-2.5 w-2.5 text-white" strokeWidth={3.5} /> : null}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
                {TERM_LABELS[opt.term]}
              </span>
            </div>

            <span className="mt-2.5 text-2xl font-bold tracking-tight text-zinc-900 sm:text-[1.7rem]">
              {formatPlanPrice(opt.priceInr)}
            </span>
            <span className="mt-1 text-xs text-zinc-500">
              {opt.months > 1 ? (
                <>
                  <span className="font-semibold text-emerald-700">{formatPlanPrice(ppm)}</span> / month
                </>
              ) : (
                'One-time payment'
              )}
            </span>
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-zinc-400">
              <Calendar className="h-3 w-3" />
              {opt.months === 1 ? '1 month' : `${opt.months} months`} access
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function PlanCheckoutSummary({
  plan,
  term,
  compact,
}: {
  plan: ApiPlan | null | undefined;
  term: BillingTerm;
  compact?: boolean;
}) {
  const opt = getPricingOptions(plan).find((o) => o.term === term);
  if (!opt || !plan) return null;

  const ppm = perMonthEquivalent(opt.priceInr, opt.months);
  const monthlyRef = getPricingOptions(plan).find((o) => o.term === 'monthly');
  const savings =
    monthlyRef && opt.term !== 'monthly' ? termSavingsPercent(monthlyRef.priceInr, opt.priceInr, opt.months) : null;

  if (compact) {
    return (
      <p className="text-sm text-zinc-600">
        <span className="font-semibold text-zinc-900">{formatPlanPrice(opt.priceInr)}</span>
        <span className="text-zinc-500"> · {opt.label}</span>
        {opt.months > 1 ? <span className="text-zinc-400"> ({formatPlanPrice(ppm)}/mo)</span> : null}
      </p>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50/80 to-white p-4 sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Your selection</p>
          <p className="mt-1 text-sm font-medium text-zinc-800">
            {plan.name} · {opt.label}
          </p>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-2xl font-bold tracking-tight text-zinc-900 sm:text-3xl">{formatPlanPrice(opt.priceInr)}</span>
            {opt.months > 1 ? (
              <span className="text-sm text-zinc-500">({formatPlanPrice(ppm)} per month)</span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col gap-1 text-xs text-zinc-500 sm:items-end sm:text-right">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 shrink-0" />
            {opt.months === 1 ? '1 month access' : `${opt.months} months access`}
          </span>
          {plan.mediaStorageLimitMb ?? plan.storageLimitMb ? (
            <span className="inline-flex items-center gap-1">
              <HardDrive className="h-3.5 w-3.5 shrink-0" />
              {plan.mediaStorageLimitMb ?? plan.storageLimitMb} MB media uploads
            </span>
          ) : null}
          {plan.databaseStorageLimitMb ? (
            <span className="inline-flex items-center gap-1">
              <HardDrive className="h-3.5 w-3.5 shrink-0 text-violet-500" />
              {plan.databaseStorageLimitMb} MB database storage
            </span>
          ) : null}
          {savings != null && savings > 0 ? (
            <span className="font-semibold text-emerald-700">You save {savings}% vs monthly</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** @deprecated use PlanCheckoutSummary */
export const PlanPriceSummary = PlanCheckoutSummary;
